import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ToolTip from "../../../../parts/tooltip";
import SvgIcon from "../../../../parts/SvgIcon";
import { preserveScrollBeforeNavigation } from "../../../../utils/preserve-scroll-on-navigation";
import { isTemplateProcessing } from "../../../../utils/meal-planner-helper";
import AIStatusPill from "../../../../parts/ai-status-pill";
import MealPlanTemplateProcessing from "./MealPlanTemplateProcessing";
import submit from "submit-form";

export const MealPlanCardDetailed = ({
  template,
  heightClass,
  dropdownRef,
  openOptions,
  setOpenOptions,
  handleOptions,
  handleDeleteWeek,
  handleDuplicateWeek,
  handleAddWeek,
  handleTemplateAction,
  cardListIcon,
  footerListIcon,
  IDsThatPreserveScroll,
}) => {
  const { t } = useTranslation("coaching-portal");
  const {
    weeks = [],
    title = "",
    description = "",
    _id: templateId,
  } = template;
  const isProcessing = isTemplateProcessing(template);
  const generation = template?.generation;

  /**
   * Function to generate grocery list for a week
   * @param {*} templateId
   * @param {*} week
   */
  const handleGenerateGroceryList = (templateId, week) => {
    submit(
      `/actions/meal-planner/generate-grocery-list?mealPlanId=${templateId}&week=${week}`,
      {
        method: "POST",
      },
    );
  };

  return (
    <article
      data-col="1/3"
      className={`card meal-plan-card--detailed ${
        isProcessing ? "meal-plan-processing gradient-border-wrapper" : ""
      }`}
    >
      <div>
        <div className="details">
          {isProcessing ? (
            <MealPlanTemplateProcessing template={template} />
          ) : (
            <>
              <header className={`text ${heightClass}`}>
                <span className="title">{title}</span>
                <span className="subtitle">{description}</span>
                {generation?.status && (
                  <div className="flex items-center gap-2 shrink-0">
                    <AIStatusPill
                      status={generation.status}
                      error={generation.error}
                    />
                  </div>
                )}
              </header>
              <ul className="weeks">
                {weeks.map((week, index) => {
                  const editUrl =
                    index === 0
                      ? `/coaching-portal/templates/edit-meal-plan?mealPlan=${templateId}`
                      : `/coaching-portal/templates/edit-meal-plan?mealPlan=${templateId}&week=${
                          index + 1
                        }`;

                  return (
                    <li key={`${index}-${templateId}`}>
                      <div className="text">
                        <div className="number">
                          <span>
                            {index + 1}/{weeks.length}
                          </span>
                        </div>
                        <div className="week">{t("allTemplates.mealPlanTemplates.week")} {index + 1}</div>
                      </div>
                      <div className="icons">
                        <a
                          role="button"
                          data-button-type="secondary"
                          data-icon
                          data-icon-only
                          className="edit"
                          onClick={preserveScrollBeforeNavigation}
                          href={`/actions/refresh?modal[type]=copy-meal-plan-to&templateId=${templateId}&week=${index}`}
                        >
                          <SvgIcon
                            src="/img/new-icons/copy-to.svg"
                            alt="edit"
                          />
                        </a>
                        <a
                          role="button"
                          data-button-type="secondary"
                          data-icon
                          data-icon-only
                          className="edit"
                          href={editUrl}
                        >
                          <SvgIcon
                            src="/img/new-icons/edit-pencil.svg"
                            alt="edit"
                          />
                        </a>
                        <details ref={dropdownRef} role="list" open>
                          <summary
                            onClick={(e) => handleOptions(e, templateId, index)}
                          >
                            <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
                          </summary>
                        </details>
                        {openOptions?.templateId === templateId &&
                          openOptions?.weekIndex === index && (
                            <ul
                              className="meal-plan-templates-options"
                              role="listbox"
                              style={{
                                top: `${openOptions.position.top}px`,
                                left: `${openOptions.position.left}px`,
                              }}
                            >
                              {cardListIcon.map(({ src, alt, id, message }) => {
                                return (
                                  <li key={`${templateId}-${id}`}>
                                    <a
                                      href={""}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (id === "delete-week") {
                                          handleDeleteWeek(templateId, index);
                                        } else if (id === "duplicate-week") {
                                          handleDuplicateWeek(
                                            templateId,
                                            index,
                                          );
                                        } else if (id === "grocery-list") {
                                          handleGenerateGroceryList(
                                            templateId,
                                            index,
                                          );
                                        }
                                        setOpenOptions(null);
                                      }}
                                    >
                                      <SvgIcon
                                        src={src}
                                        alt={alt}
                                        noFilterOnLight={id === "delete-week"}
                                      />
                                      {message}
                                    </a>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        {!isProcessing && (
          <footer className="footer">
            <button
              className="edit-card"
              data-button-type="text"
              data-icon
              sz="small"
              onClick={() => {
                handleAddWeek(templateId);
              }}
            >
              <img alt={t("allTemplates.mealPlanCardSimplified.addAlt")} src="/img/new-icons/edit-gradient-icon.svg" />
              <span className="font-semibold">{t("allTemplates.mealPlanTemplates.addWeek")}</span>
            </button>
            <div className="icons">
              {footerListIcon.map(({ src, alt, id, url, message }) => {
                const finalUrl =
                  id === "copy-meal-plan-from" ||
                  id === "copy-meal-plan-to" ||
                  id === "meal-plan-settings"
                    ? `${url}&templateId=${templateId}`
                    : id === "preview-template"
                      ? `${url}?mealPlan=${templateId}&viewMode=true`
                      : url;

                return (
                  <ToolTip
                    id={`${templateId}-${id}`}
                    body={message}
                    key={`${templateId}-${id}`}
                  >
                    <a
                      href={
                        ["delete-template", "duplicate-template"].includes(id)
                          ? ""
                          : finalUrl
                      }
                      onClick={(e) => {
                        if (
                          ["delete-template", "duplicate-template"].includes(id)
                        ) {
                          e.preventDefault();
                          handleTemplateAction(templateId, id);
                        }
                        if (IDsThatPreserveScroll.includes(id)) {
                          preserveScrollBeforeNavigation();
                        }
                      }}
                    >
                      <div
                        className="bg-(--white-05) p-1 rounded-(--border-radius-md) hover:bg-(--white-20) cursor-pointer"
                        id="img-footer-cycle-card-container"
                      >
                        <SvgIcon
                          src={src}
                          alt={alt}
                          noFilterOnLight={id === "delete-template"}
                        />
                      </div>
                    </a>
                  </ToolTip>
                );
              })}
            </div>
          </footer>
        )}
      </div>
    </article>
  );
};

MealPlanCardDetailed.propTypes = {
  template: PropTypes.object.isRequired,
  heightClass: PropTypes.string.isRequired,
  dropdownRef: PropTypes.object.isRequired,
  openOptions: PropTypes.object,
  setOpenOptions: PropTypes.func.isRequired,
  handleOptions: PropTypes.func.isRequired,
  handleDeleteWeek: PropTypes.func.isRequired,
  handleDuplicateWeek: PropTypes.func.isRequired,
  handleAddWeek: PropTypes.func.isRequired,
  handleTemplateAction: PropTypes.func.isRequired,
  cardListIcon: PropTypes.array.isRequired,
  footerListIcon: PropTypes.array.isRequired,
  IDsThatPreserveScroll: PropTypes.array.isRequired,
};
